/**
 * SearchPage
 *
 * Email search with advanced filters and results displayed as message list items.
 */

import { useState, useCallback } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useEmailAccounts } from '../hooks/useEmailAccounts';
import { useEmailSearch } from '../hooks/useEmailSearch';
import { useFolders } from '../hooks/useFolders';
import { useLabels } from '../hooks/useLabels';
import { MessageListItem } from '../components/MessageListItem';
import { ReadingPane } from '../components/ReadingPane';

export function SearchPage() {
  const { activeAccount } = useEmailAccounts();
  const accountId = activeAccount?.id;

  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [hasAttachment, setHasAttachment] = useState<boolean | undefined>(undefined);
  const [isStarred, setIsStarred] = useState<boolean | undefined>(undefined);
  const [folder, setFolder] = useState('');
  const [label, setLabel] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [importance, setImportance] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { folders } = useFolders(accountId);
  const { labels } = useLabels(accountId);

  const { messages, total, loading } = useEmailSearch({
    accountId,
    q: query || undefined,
    from: from || undefined,
    to: to || undefined,
    subject: subject || undefined,
    hasAttachment,
    isStarred,
    folder: folder || undefined,
    label: label || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    importance: (importance as 'low' | 'normal' | 'high') || undefined,
    enabled: submitted,
  });

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setActiveMessageId(null);
  }, []);

  const clearFilters = useCallback(() => {
    setFrom('');
    setTo('');
    setSubject('');
    setHasAttachment(undefined);
    setIsStarred(undefined);
    setFolder('');
    setLabel('');
    setDateFrom('');
    setDateTo('');
    setImportance('');
  }, []);

  return (
    <div className="flex h-full">
      {/* Left: search + results */}
      <div className="flex flex-col flex-1 min-w-0 max-w-lg border-r border-border">
        {/* Search bar */}
        <form onSubmit={handleSearch} className="p-3 border-b border-border space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSubmitted(false);
                }}
                placeholder="Search emails..."
                className="pl-9"
              />
            </div>
            <Button type="submit" size="sm">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(showFilters && 'bg-muted')}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>

          {/* Advanced filters */}
          <Collapsible open={showFilters}>
            <CollapsibleContent className="space-y-2 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="h-8 text-sm"
                    placeholder="sender"
                  />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    className="h-8 text-sm"
                    placeholder="recipient"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Date from</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date to</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Folder</Label>
                  <Select value={folder} onValueChange={setFolder}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Any</SelectItem>
                      {folders.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Label</Label>
                  <Select value={label} onValueChange={setLabel}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Any</SelectItem>
                      {labels.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Importance</Label>
                  <Select value={importance} onValueChange={setImportance}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Any</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={hasAttachment === true}
                      onCheckedChange={(v) => setHasAttachment(v ? true : undefined)}
                    />
                    <Label className="text-xs">Has attachment</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isStarred === true}
                      onCheckedChange={(v) => setIsStarred(v ? true : undefined)}
                    />
                    <Label className="text-xs">Starred</Label>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs gap-1">
                <X className="h-3 w-3" /> Clear filters
              </Button>
            </CollapsibleContent>
          </Collapsible>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {!submitted ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Search className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm">Enter a search query</p>
            </div>
          ) : loading ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-10">
              <Search className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm">No results found</p>
            </div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border">
                {total} result{total !== 1 ? 's' : ''}
              </div>
              {messages.map((msg) => (
                <MessageListItem
                  key={msg.id}
                  id={msg.id}
                  fromName={msg.fromName}
                  fromAddress={msg.fromAddress}
                  subject={msg.subject}
                  snippet={msg.snippet}
                  date={msg.sentAt || msg.createdAt}
                  isRead={msg.isRead}
                  isStarred={msg.isStarred}
                  hasAttachments={msg.hasAttachments}
                  isSelected={false}
                  isActive={msg.id === activeMessageId}
                  onSelect={() => {}}
                  onClick={(id) => setActiveMessageId(id)}
                  onToggleStar={() => {}}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Right: reading pane */}
      <div className="flex-1 min-w-0 hidden lg:block">
        <ReadingPane messageId={activeMessageId} accountId={accountId} />
      </div>
    </div>
  );
}
